/**
 * Keeps a consultation call usable on a weak connection.
 *
 * When video fills a weak link, audio packets queue behind it and the voice
 * lags or breaks up. So when LiveKit reports this device's connection as weak,
 * video steps down one level at a time and finally stops, leaving the whole
 * link to the voice:
 *
 *   weak for 8 s        → send at most 360p
 *   weak 10 s more      → send at most 180p
 *   weak 10 s more      → audio only: the camera is unpublished (and released),
 *                         the other side's video is unsubscribed, the mic stays on
 *   good for 30 s       → step back up one level (360p → 720p); on audio only,
 *                         offer "Turn video back on" — the camera is never
 *                         turned back on without the person asking
 *
 * Each side tells the other when it goes audio only (a LiveKit data message),
 * and reports switches and reconnects for the audit trail.
 *
 * It also brings the camera back after a hardware drop-out. When a camera ends
 * mid-call (unplugged, a USB hiccup) LiveKit tries another camera and, if none
 * is there, turns the camera off — and nothing turned it back on once a camera
 * was reconnected, leaving a black tile. This controller notices the drop-out,
 * says so (cameraLost), and re-enables the camera as soon as one is available.
 * A camera the person turned off themselves is left off.
 *
 * One copy lives here (patient app, native and web); the doctor portal keeps
 * an identical copy at doctor-portal/src/lib/call/call-quality.ts. Change both.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  LocalTrackPublication,
  LocalVideoTrack,
  Participant,
  RemoteParticipant,
  RemoteTrackPublication,
  Room,
} from 'livekit-client';

// livekit-client's enum values, written out. On native the SDK may only be
// loaded after registerGlobals() (lib/call/livekit.ts), so this module imports
// its types and nothing else.
const ROOM_EVENT = {
  qualityChanged: 'connectionQualityChanged',
  reconnecting: 'reconnecting',
  signalReconnecting: 'signalReconnecting',
  reconnected: 'reconnected',
  dataReceived: 'dataReceived',
  trackPublished: 'trackPublished',
  localTrackPublished: 'localTrackPublished',
  participantConnected: 'participantConnected',
  participantDisconnected: 'participantDisconnected',
  mediaDevicesChanged: 'mediaDevicesChanged',
} as const;
const TRACK_ENDED = 'ended';
const CAMERA = 'camera';
// VideoQuality: LOW = 0, MEDIUM = 1, HIGH = 2.
const MAX_QUALITY_FOR_LEVEL = [2, 1, 0] as const;
const DATA_TOPIC = 'kyros.call-mode';

export const LADDER_TIMING = {
  firstStepDownMs: 8_000,
  nextStepDownMs: 10_000,
  stepUpMs: 30_000,
  videoEstablishedMs: 30_000,
  videoEstablishedRetryMs: 10_000,
  tickMs: 1_000,
  /** While the camera is lost, look for one this often (devicechange can be missed). */
  cameraRetryMs: 5_000,
} as const;

/** 0 = up to 720p, 1 = up to 360p, 2 = up to 180p, 3 = audio only. */
export type QualityLevel = 0 | 1 | 2 | 3;
export type Signal = 'excellent' | 'good' | 'poor' | 'lost' | 'unknown';
export type AudioOnlyReason = 'weak' | 'manual';
export type CallEventName = 'audio_only_on' | 'audio_only_off' | 'reconnected';
export type CallEventReporter = (event: CallEventName, trigger?: 'auto' | 'manual') => void;

export interface CallQualityState {
  level: QualityLevel;
  /** This device's connection, as LiveKit's server measures it. */
  signal: Signal;
  reconnecting: boolean;
  /** Why this side is on audio only; null when video is on. */
  audioOnlyReason: AudioOnlyReason | null;
  /** On audio only after a weak spell, and the connection has been good for 30 s. */
  videoCanReturn: boolean;
  /** Why the other side (the doctor, or the patient) is on audio only, if they are. */
  peerAudioOnly: AudioOnlyReason | null;
  /** The camera dropped out mid-call and no camera has taken over yet. */
  cameraLost: boolean;
}

export const INITIAL_CALL_QUALITY: CallQualityState = {
  level: 0,
  signal: 'unknown',
  reconnecting: false,
  audioOnlyReason: null,
  videoCanReturn: false,
  peerAudioOnly: null,
  cameraLost: false,
};

export interface CallQualityOptions {
  /** Identity of the other main participant: 'doctor' in the patient app, 'patient' in the portal. */
  peerIdentity: string;
  report: CallEventReporter;
  /** Doctor portal only: called once both cameras have been live together for
   *  30 s. A rejected promise means "not recorded" and it is tried again. */
  onVideoEstablished?: () => Promise<unknown> | void;
}

/** Signal strength as bars, for the indicator. */
export function signalBars(signal: Signal): 0 | 1 | 2 | 3 {
  if (signal === 'excellent') return 3;
  if (signal === 'good') return 2;
  if (signal === 'poor') return 1;
  return 0;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- LiveKit event handlers have per-event signatures
type Handler = (...args: any[]) => void;
interface Emitter {
  on(event: string, fn: Handler): unknown;
  off(event: string, fn: Handler): unknown;
}

// Payloads are short ASCII JSON, so no TextEncoder/TextDecoder is needed
// (Hermes has no TextDecoder).
function encode(value: object): Uint8Array {
  const text = JSON.stringify(value);
  return Uint8Array.from(text, (c) => c.charCodeAt(0));
}
function decode(bytes: Uint8Array): unknown {
  try {
    return JSON.parse(String.fromCharCode(...bytes));
  } catch {
    return null;
  }
}

export class CallQualityController {
  private state: CallQualityState = INITIAL_CALL_QUALITY;
  private weakSince: number | null = null;
  private goodSince: number | null = null;
  private bothCamerasSince: number | null = null;
  private videoEstablished: 'idle' | 'sending' | 'sent' = 'idle';
  private lastVideoEstablishedAttempt = Number.NEGATIVE_INFINITY;
  private switching = false;
  private peerModes = new Map<string, AudioOnlyReason>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private listeners: Array<[string, Handler]> = [];
  private watchedCameraTracks = new WeakSet<object>();
  /** A camera track ended on its own (not a mute or an audio-only switch). */
  private cameraDropped = false;
  private lastCameraRetry = 0;

  constructor(
    private readonly room: Room,
    private readonly opts: CallQualityOptions,
    private readonly onChange: (state: CallQualityState) => void,
    private readonly now: () => number = Date.now,
  ) {}

  start(): void {
    this.listen(ROOM_EVENT.qualityChanged, (quality: string, participant: Participant) => {
      if (participant.isLocal) this.set({ signal: quality as Signal });
    });
    this.listen(ROOM_EVENT.reconnecting, () => this.onReconnecting());
    this.listen(ROOM_EVENT.signalReconnecting, () => this.onReconnecting());
    this.listen(ROOM_EVENT.reconnected, () => {
      this.set({ reconnecting: false });
      this.opts.report('reconnected');
      if (this.state.level === 3) this.broadcast();
    });
    this.listen(ROOM_EVENT.dataReceived, (
      payload: Uint8Array,
      participant?: RemoteParticipant,
      _kind?: unknown,
      topic?: string,
    ) => {
      if (topic !== DATA_TOPIC || !participant) return;
      const msg = decode(payload) as { mode?: string; reason?: string } | null;
      if (msg?.mode === 'audio') {
        this.peerModes.set(participant.identity, msg.reason === 'manual' ? 'manual' : 'weak');
      } else {
        this.peerModes.delete(participant.identity);
      }
      this.syncPeer();
    });
    this.listen(ROOM_EVENT.trackPublished, (pub: RemoteTrackPublication) => {
      if (this.state.level === 3 && String(pub.source) === CAMERA) pub.setSubscribed(false);
    });
    this.listen(ROOM_EVENT.localTrackPublished, (pub: LocalTrackPublication) => {
      if (String(pub.source) !== CAMERA) return;
      this.watchCamera(pub);
      if (this.state.level < 3) {
        this.applyCap();
      } else if (pub.track) {
        // turnVideoBackOn leaves level 3 before enabling the camera, so a camera
        // published while still on audio only came from somewhere else (the
        // room re-applying its `video` prop after a reconnect). Keep it off.
        this.room.localParticipant.unpublishTrack(pub.track, true).catch(() => undefined);
      }
    });
    this.listen(ROOM_EVENT.participantConnected, () => {
      if (this.state.level === 3) this.broadcast();
    });
    this.listen(ROOM_EVENT.participantDisconnected, (participant: RemoteParticipant) => {
      this.peerModes.delete(participant.identity);
      this.syncPeer();
    });
    this.listen(ROOM_EVENT.mediaDevicesChanged, () => {
      void this.recoverCamera();
    });
    const existing = this.cameraPublication();
    if (existing) this.watchCamera(existing);
    this.timer = setInterval(() => this.tick(), LADDER_TIMING.tickMs);
  }

  dispose(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    const emitter = this.room as unknown as Emitter;
    for (const [event, fn] of this.listeners) emitter.off(event, fn);
    this.listeners = [];
  }

  /** The Audio only button. */
  async goAudioOnly(): Promise<void> {
    await this.enterAudioOnly('manual');
  }

  /** "Turn video back on" — always the person's own tap. */
  async turnVideoBackOn(): Promise<void> {
    if (this.state.level !== 3 || this.switching) return;
    this.switching = true;
    // `finally`: a throw anywhere here must not leave `switching` set, which
    // would freeze the ladder for the rest of the call.
    try {
      // After a weak spell, resume at 360p and let the ladder climb; after a
      // manual switch the connection was fine, so go straight back to 720p.
      const resume: QualityLevel = this.state.audioOnlyReason === 'weak' ? 1 : 0;
      this.weakSince = null;
      this.goodSince = null;
      this.set({ level: resume, audioOnlyReason: null, videoCanReturn: false });
      this.setPeerVideo(true);
      try {
        await this.room.localParticipant.setCameraEnabled(true);
      } catch {
        // Camera unavailable: audio continues; the person can retry.
      }
      this.applyCap();
      this.broadcast();
      this.opts.report('audio_only_off', 'manual');
    } finally {
      this.switching = false;
    }
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  private listen(event: string, fn: Handler): void {
    (this.room as unknown as Emitter).on(event, fn);
    this.listeners.push([event, fn]);
  }

  private set(patch: Partial<CallQualityState>): void {
    const next = { ...this.state, ...patch };
    const changed = (Object.keys(patch) as Array<keyof CallQualityState>).some(
      (k) => next[k] !== this.state[k],
    );
    if (!changed) return;
    this.state = next;
    this.onChange(next);
  }

  private onReconnecting(): void {
    // Nothing sent during a reconnect gets through; judge the link afresh after.
    this.weakSince = null;
    this.goodSince = null;
    this.set({ reconnecting: true });
  }

  private tick(): void {
    const now = this.now();
    this.checkVideoEstablished(now);
    this.checkCamera(now);
    if (this.state.reconnecting || this.switching) return;

    const { signal, level } = this.state;
    const weak = signal === 'poor' || signal === 'lost';
    const good = signal === 'good' || signal === 'excellent';

    if (weak) {
      this.goodSince = null;
      this.weakSince ??= now;
      const weakFor = now - this.weakSince;
      if (level === 0 && weakFor >= LADDER_TIMING.firstStepDownMs) {
        this.stepTo(1);
      } else if (level === 1 && weakFor >= LADDER_TIMING.nextStepDownMs) {
        this.stepTo(2);
      } else if (level === 2 && weakFor >= LADDER_TIMING.nextStepDownMs) {
        void this.enterAudioOnly('weak');
      }
      return;
    }

    this.weakSince = null;
    if (!good) {
      this.goodSince = null;
      return;
    }
    this.goodSince ??= now;
    if (now - this.goodSince < LADDER_TIMING.stepUpMs) return;
    this.goodSince = now;
    if (level === 1 || level === 2) {
      this.stepTo(level === 2 ? 1 : 0);
    } else if (level === 3 && this.state.audioOnlyReason === 'weak') {
      this.set({ videoCanReturn: true });
    }
  }

  private stepTo(level: 0 | 1 | 2): void {
    this.weakSince = null;
    this.set({ level });
    this.applyCap();
  }

  private async enterAudioOnly(reason: AudioOnlyReason): Promise<void> {
    if (this.state.level === 3 || this.switching) return;
    this.switching = true;
    try {
      this.weakSince = null;
      this.goodSince = null;
      this.set({ level: 3, audioOnlyReason: reason, videoCanReturn: false });
      const pub = this.cameraPublication();
      if (pub?.track) {
        try {
          // Unpublish rather than mute: the camera itself is released, and
          // nothing about video is negotiated while the link is struggling.
          await this.room.localParticipant.unpublishTrack(pub.track, true);
        } catch {
          // Already gone.
        }
      }
      this.setPeerVideo(false);
      this.broadcast();
      this.opts.report('audio_only_on', reason === 'weak' ? 'auto' : 'manual');
    } finally {
      this.switching = false;
    }
  }

  private watchCamera(pub: LocalTrackPublication): void {
    const track = pub.track as unknown as Emitter | undefined;
    if (!track || this.watchedCameraTracks.has(track)) return;
    this.watchedCameraTracks.add(track);
    // Fires only when the device stops on its own — a mute or an unpublish
    // stops the track without this event.
    track.on(TRACK_ENDED, () => {
      if (this.state.level < 3) this.cameraDropped = true;
    });
  }

  /** After a drop-out: lost while LiveKit has it muted, cleared once it is live again. */
  private checkCamera(now: number): void {
    if (!this.cameraDropped) return;
    const pub = this.cameraPublication();
    const live = Boolean(pub?.track) && !pub?.isMuted;
    if (live || this.state.level === 3) {
      // LiveKit switched to another camera by itself, or audio only took over.
      this.cameraDropped = false;
      this.set({ cameraLost: false });
      return;
    }
    this.set({ cameraLost: true });
    if (now - this.lastCameraRetry >= LADDER_TIMING.cameraRetryMs) void this.recoverCamera();
  }

  private async recoverCamera(): Promise<void> {
    if (!this.cameraDropped || this.state.level === 3 || this.switching) return;
    this.lastCameraRetry = this.now();
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (!devices.some((d) => d.kind === 'videoinput')) return;
      await this.room.localParticipant.setCameraEnabled(true);
      this.cameraDropped = false;
      this.set({ cameraLost: false });
      this.applyCap();
    } catch {
      // Still no usable camera; the next device change or retry tries again.
    }
  }

  private cameraPublication(): LocalTrackPublication | undefined {
    for (const pub of this.room.localParticipant.trackPublications.values()) {
      if (String(pub.source) === CAMERA) return pub;
    }
    return undefined;
  }

  private applyCap(): void {
    const { level } = this.state;
    if (level === 3) return;
    const track = this.cameraPublication()?.videoTrack as LocalVideoTrack | undefined;
    track?.setPublishingQuality(
      MAX_QUALITY_FOR_LEVEL[level] as unknown as Parameters<LocalVideoTrack['setPublishingQuality']>[0],
    );
  }

  private setPeerVideo(subscribed: boolean): void {
    for (const participant of this.room.remoteParticipants.values()) {
      for (const pub of participant.trackPublications.values()) {
        if (String(pub.source) === CAMERA) pub.setSubscribed(subscribed);
      }
    }
  }

  private broadcast(): void {
    const audio = this.state.level === 3;
    const message = audio
      ? { mode: 'audio', reason: this.state.audioOnlyReason ?? 'manual' }
      : { mode: 'video' };
    this.room.localParticipant
      .publishData(encode(message), { reliable: true, topic: DATA_TOPIC })
      .catch(() => undefined);
  }

  private syncPeer(): void {
    this.set({ peerAudioOnly: this.peerModes.get(this.opts.peerIdentity) ?? null });
  }

  private checkVideoEstablished(now: number): void {
    const report = this.opts.onVideoEstablished;
    if (!report || this.videoEstablished !== 'idle') return;
    const local = this.cameraPublication();
    const localLive = Boolean(local?.track) && !local?.isMuted;
    const peer = [...this.room.remoteParticipants.values()].find(
      (p) => p.identity === this.opts.peerIdentity,
    );
    let peerLive = false;
    if (peer) {
      for (const pub of peer.trackPublications.values()) {
        if (String(pub.source) === CAMERA && pub.isSubscribed && pub.track && !pub.isMuted) {
          peerLive = true;
        }
      }
    }
    if (!localLive || !peerLive) {
      this.bothCamerasSince = null;
      return;
    }
    this.bothCamerasSince ??= now;
    if (now - this.bothCamerasSince < LADDER_TIMING.videoEstablishedMs) return;
    if (now - this.lastVideoEstablishedAttempt < LADDER_TIMING.videoEstablishedRetryMs) return;
    // Marked sent only once the report succeeds: a lost request used to leave
    // the List A warning up for a visit that had video.
    this.videoEstablished = 'sending';
    this.lastVideoEstablishedAttempt = now;
    Promise.resolve()
      .then(report)
      .then(
        () => { this.videoEstablished = 'sent'; },
        () => { this.videoEstablished = 'idle'; },
      );
  }
}

/** React binding: runs the ladder for the room it is given (from useRoomContext). */
export function useCallQuality(
  room: Room | undefined,
  opts: CallQualityOptions,
): {
  state: CallQualityState;
  goAudioOnly: () => void;
  turnVideoBackOn: () => void;
} {
  const [state, setState] = useState<CallQualityState>(INITIAL_CALL_QUALITY);
  const controller = useRef<CallQualityController | null>(null);
  // Callbacks change identity on every render; the controller reads the latest.
  const latest = useRef(opts);
  latest.current = opts;

  useEffect(() => {
    if (!room) return;
    const c = new CallQualityController(
      room,
      {
        peerIdentity: opts.peerIdentity,
        report: (event, trigger) => latest.current.report(event, trigger),
        onVideoEstablished: opts.onVideoEstablished
          ? () => latest.current.onVideoEstablished?.()
          : undefined,
      },
      setState,
    );
    controller.current = c;
    c.start();
    return () => {
      c.dispose();
      controller.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one controller per room
  }, [room]);

  const goAudioOnly = useCallback(() => void controller.current?.goAudioOnly(), []);
  const turnVideoBackOn = useCallback(() => void controller.current?.turnVideoBackOn(), []);
  return { state, goAudioOnly, turnVideoBackOn };
}
